import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { DisplayGuestDto, DisplayUserDto, VisitorDto } from '@picflow/types';
import { lastValueFrom } from 'rxjs';
import { reviewersCacheMaxAge } from '../consts';
import { AppConfigService } from '../providers/config.service';
import { DataLoaderService } from './data-loader.service';
import { FeatureFlagsService } from './feature-flags.service';
import { GalleryPasswordService } from './gallery-password.service';
import { StateService } from './state.service';

export interface DisplayUserEmailDto extends DisplayUserDto {
  email: string;
}

export interface DisplayGuestEmailDto extends DisplayGuestDto {
  email: string;
}

export interface visitorResponse extends Omit<VisitorDto, 'guest' | 'user'> {
  user: DisplayUserEmailDto;
  guest: DisplayGuestEmailDto;
}

@Injectable({
  providedIn: 'root',
})
export class VisitorService {
  private http = inject(HttpClient);
  private config = inject(AppConfigService);
  private loader = inject(DataLoaderService);
  private state = inject(StateService);
  private featureFlags = inject(FeatureFlagsService);
  private galleryPasswordService = inject(GalleryPasswordService);

  public async getAllVisitors() {
    const url = `${this.config.apiEndpoint}/visitors?expand[]=data.user&expand[]=data.guest`;
    return await this.loader.get('visitors', async () => this.getVisitorsInternal(url), reviewersCacheMaxAge);
  }

  public async getGalleryVisitors(galleryId: string) {
    const url = `${this.config.apiEndpoint}/visitors?expand[]=data.user&expand[]=data.guest&gallery=${galleryId}`;
    return await this.loader.get(
      `visitors_${galleryId}`,
      async () => this.getVisitorsInternal(url),
      reviewersCacheMaxAge
    );
  }

  private getVisitorsInternal = async (url: string, visitors = [], page = 0) => {
    const limit = 100;
    const resp = await lastValueFrom(this.http.get<{ data: visitorResponse[] }>(`${url}&limit=${limit}&page=${page}`));
    visitors.push(...resp.data);
    if (resp.data.length === limit) return this.getVisitorsInternal(url, visitors, page + 1);
    return this.makeUnique(visitors);
  };

  public async createVisitor(galleryId: string) {
    const email = this.state.user?.email;
    if (email && ['@nusign.com', '@picflow.com'].find(_email => email.endsWith(_email))) {
      // preventing internal views as counting unless spesifically allowed
      if (!this.featureFlags.get('forceGalleryViews')) return;
    }
    // using loader just to make sure that we don't do this request multiple times per session
    return await this.loader.get(`create_visitor_${galleryId}`, async () => {
      try {
        return await lastValueFrom(
          this.http.post(`${this.config.apiEndpoint}/visitors${this.getPassword(galleryId)}`, { gallery: galleryId })
        );
      } catch (err) {
        if (!(err instanceof HttpErrorResponse)) throw err;
        if (err.status !== 400) throw err;
        if (err.error.code !== 'visitor_exists') throw err;
        return true;
      }
    });
  }

  private makeUnique(visitors: visitorResponse[]) {
    const unique = [];
    visitors.forEach(visitor => {
      if (
        !unique.find(_visitor => {
          if (visitor.user && _visitor.user) return _visitor.user.id === visitor.user.id;
          if (visitor.guest && _visitor.guest) return _visitor.guest.id === visitor.guest.id;
          return false;
        })
      ) {
        unique.push(visitor);
      }
    });
    return unique;
  }

  private getPassword(galleryId: string) {
    const password = this.galleryPasswordService.getPassword(galleryId);
    if (!password) return '';
    return '?password=' + encodeURIComponent(password);
  }
}
